import { useState } from 'react';
import { X, Search, Check, Send, Users } from 'lucide-react';
import { getAllUsers, type UserProfile } from '@/store/users';
import UserAvatar from '@/components/UserAvatar';
import { cn } from '@/lib/utils';

interface RecipientPickerProps {
  open: boolean;
  onClose: () => void;
  currentUserId: string;
  onSend: (userIds: string[], mode: 'individual' | 'group') => void;
}

const RecipientPicker = ({ open, onClose, currentUserId, onSend }: RecipientPickerProps) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  if (!open) return null;

  const allUsers = getAllUsers().filter(
    (u) => u.id !== currentUserId && !u.banned && u.role !== 'admin'
  );

  const filtered = search.trim()
    ? allUsers.filter(
        (u) =>
          u.name.toLowerCase().includes(search.toLowerCase()) ||
          u.username.toLowerCase().includes(search.toLowerCase())
      )
    : allUsers;

  const toggle = (userId: string) => {
    setSelected((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSend = (mode: 'individual' | 'group') => {
    if (selected.length === 0) return;
    onSend(selected, mode);
    setSelected([]);
    setSearch('');
  };

  const handleClose = () => {
    setSelected([]);
    setSearch('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60" onClick={handleClose}>
      <div
        className="mx-auto flex w-full max-w-lg flex-col rounded-t-2xl bg-card animate-fade-in"
        style={{ maxHeight: '80vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="text-base font-semibold text-foreground">Send to</h3>
          <button onClick={handleClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 rounded-full bg-secondary px-4 py-2.5">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users..."
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              autoFocus
            />
          </div>
        </div>

        {/* Selected chips */}
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-2 px-4 pb-2">
            {selected.map((id) => {
              const user = allUsers.find((u) => u.id === id);
              return (
                <button
                  key={id}
                  onClick={() => toggle(id)}
                  className="flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-xs font-medium text-primary"
                >
                  {user?.name ?? 'Unknown'}
                  <X className="h-3 w-3" />
                </button>
              );
            })}
          </div>
        )}

        {/* User list */}
        <div className="flex-1 overflow-y-auto px-2 pb-2">
          {filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No users found</p>
          ) : (
            filtered.map((user) => {
              const isSelected = selected.includes(user.id);
              return (
                <button
                  key={user.id}
                  onClick={() => toggle(user.id)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors',
                    isSelected ? 'bg-primary/10' : 'hover:bg-secondary'
                  )}
                >
                  <UserAvatar
                    avatarUrl={user.avatarUrl}
                    initials={user.initials}
                    className="h-10 w-10"
                    fallbackClassName="text-xs"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">@{user.username}</p>
                  </div>
                  <div
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors',
                      isSelected
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground/30'
                    )}
                  >
                    {isSelected && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Send buttons */}
        <div className="border-t border-border px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {selected.length === 0 && (
            <button
              disabled
              className="flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold bg-secondary text-muted-foreground"
            >
              <Send className="h-4 w-4" />
              Select recipients
            </button>
          )}
          {selected.length === 1 && (
            <button
              onClick={() => handleSend('individual')}
              className="flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold bg-primary text-primary-foreground shadow-md shadow-primary/20 press-scale transition-all"
            >
              <Send className="h-4 w-4" />
              Send to 1 person
            </button>
          )}
          {selected.length > 1 && (
            <div className="flex gap-2">
              <button
                onClick={() => handleSend('individual')}
                className="flex flex-1 items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold bg-primary text-primary-foreground shadow-md shadow-primary/20 press-scale transition-all"
              >
                <Send className="h-4 w-4" />
                Send individually
              </button>
              <button
                onClick={() => handleSend('group')}
                className="flex flex-1 items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold bg-secondary text-foreground border border-border press-scale transition-all hover:bg-accent"
              >
                <Users className="h-4 w-4" />
                Create group
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipientPicker;
